<template>
  <StandardCardPageContainer :includeNavigation="true" :loading="isLoading">
    <template #title>Reuse questionnaire responses</template>
    <p>
      Choose a response you would like to share with
      {{ tlcNamesString }}.
    </p>
    <p class="mb-10">You can edit your response at a later step.</p>
    <v-radio-group v-model="selectedSourceQuestionnaireId">
      <o-table :data="mappedData" paginated>
        <o-table-column v-slot="props" class="align-start">
          <v-radio
            :value="props.row['questionnaireId']"
            density="compact"
            :color="PwcTheme.colors.primaryButton"
            @click="deselectIfSelected(props.row['questionnaireId'])"
          />
        </o-table-column>

        <o-table-column v-for="column in columns" v-bind="column">
          <template v-if="column.searchable" #searchable="props">
            <o-input
              v-model="props.filters[props.column.field]"
              placeholder="Search..."
              size="small"
            />
          </template>
          <template v-if="column.field == 'answerStatus'" #default="props">
            <QuestionnaireStatusBadge :status="props.row[column.field]" />
          </template>
          <template v-else #default="props">
            {{ props.row[column.field] }}
          </template>
        </o-table-column>
      </o-table>
    </v-radio-group>
    <PTextButton appkit-icon="icon-plus-fill" @click="startQuestionnaire"
      >Create new</PTextButton
    >

    <template #nextButton>
      <PActionButton
        appkit-icon="icon-right-chevron-fill"
        :icon-size="'xs'"
        :append-icon="true"
        @click="cloneAnswersAndContinue"
        :disabled="continueButtonDisabled"
        >Continue</PActionButton
      >
    </template>
  </StandardCardPageContainer>
</template>

<script setup lang="ts">
import StandardCardPageContainer from "@/components/containers/StandardCardPageContainer.vue";
import type ReceivedQuestionnaire from "@/models/receivedQuestionnaire";
import { getCompletedReceivedQuestionnairesForTemplateId } from "@/services/questionnairesService";
import { computed, onMounted, ref, type Ref } from "vue";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import PActionButton from "@/components/custom/buttons/PActionButton.vue";
import PTextButton from "@/components/custom/buttons/PTextButton.vue";
import { cloneAnswerSet } from "@/services/answersService";
import { RouterPath } from "@/models/enum/routerPath";
import { useRouter } from "vue-router";
import { PwcTheme } from "@/plugins/pwc-theme";
import QuestionnaireStatusBadge from "@/components/custom/QuestionnaireStatusBadge.vue";

const reusableAnswers: Ref<ReceivedQuestionnaire[]> = ref([]);
const selectedQuestionnaireStore = useSelectedQuestionnaire();
const selectedSourceQuestionnaireId = ref("");
const isLoading = ref(false);
const router = useRouter();
const continueButtonDisabled = computed(() => selectedSourceQuestionnaireId.value == "");
const tlcNamesString = ref("");

const mappedData = computed(() =>
  reusableAnswers.value.map((r) => ({
    questionnaireLabel: r.questionnaireLabel,
    senderCompanies: stringifySuppliers(r.senderCompanies),
    lastEdited: r.lastEdited,
    answerStatus: r.answerStatus,
    questionnaireId: r.questionnaireId,
  }))
);

function getTlcNames() {
  const tlcNames = [... new Set(selectedQuestionnaireStore.questionnaires.map((q) => q.tlcName).filter((n): n is string => true))];
  return getConcatenatedSenderCompanies(tlcNames.filter((n) => n != undefined));
}

function getConcatenatedSenderCompanies(senders: string[]) {
  //Replace the last , with 'and '
  //"PwC Norway, NRK, TV2 and RiksTV"
  return senders.join(", ").replace(/,(?=[^,]*$)/, " and");
}

onMounted(async () => {
  isLoading.value = true;
  reusableAnswers.value = await getCompletedReceivedQuestionnairesForTemplateId(
    selectedQuestionnaireStore.questionnaire.templateId
  );
  tlcNamesString.value = getTlcNames();
  isLoading.value = false;
});

const columns = ref([
  {
    field: "questionnaireLabel",
    label: "Questionnaire name",
    searchable: true,
    sortable: true,
  },
  {
    field: "senderCompanies",
    label: "Senders",
    searchable: true,
    sortable: true,
  },
  {
    field: "lastEdited",
    label: "Last edited",
    searchable: true,
    sortable: true,
  },
  {
    field: "answerStatus",
    label: "Status",
  },
]);

function stringifySuppliers(supplierList: string[]): string {
  return supplierList.join(", ");
}

async function cloneAnswersAndContinue() {
  await cloneAnswerSet(
    selectedSourceQuestionnaireId.value,
    selectedQuestionnaireStore.questionnaires.map((q) => q.questionnaireId)
  );
  selectedQuestionnaireStore.setReusingAnswers();
  startQuestionnaire();
}

async function startQuestionnaire() {
  router.push(RouterPath.StartQuestionnaire);
}

function deselectIfSelected(id: string) {
  if (selectedSourceQuestionnaireId.value == id) {
    selectedSourceQuestionnaireId.value = "";
  }
}
</script>
